import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  type Capacidades,
  type FaqNaLista,
  type GrupoDeLacunas,
  type ListaFaqs,
  type ListaLacunas,
  type ResultadoLacuna,
  type StatusLacuna,
  api,
  explicarErro,
} from '../lib/api.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { PreviaWhatsapp } from './PreviaWhatsapp.js';
import { BarraDeFiltros, BlocoDeAjuda, Vazio } from './ui.js';

/**
 * Fila de lacunas.
 *
 * Uma linha por GRUPO, não por lacuna: 40 perguntas sem resposta costumam ser
 * 8 dúvidas repetidas, e decidir 8 vezes é trabalho; decidir 40 é desistir.
 *
 * As candidatas ficam visíveis antes das ações porque mudam a decisão: quando
 * a mesma FAQ aparece em todas as lacunas do grupo, o caminho é acrescentar
 * variação a ela — a resposta já existe, faltava a forma de perguntar.
 */

type Acao = 'criar' | 'vincular' | 'descartar';

/** O que a tela diz depois de agir. `reindexar` liga o aviso de embedding. */
interface Aviso {
  texto: string;
  reindexar: boolean;
}

export function FilaLacunas({
  capacidades,
  aoIrParaFaqs,
}: {
  capacidades: Capacidades;
  aoIrParaFaqs: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<ListaLacunas | null>(null);
  const [status, setStatus] = useState<StatusLacuna>('pendente');
  const [dias, setDias] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  /** Resultado da última ação. Fica na tela até a próxima: é onde se avisa
   *  que a FAQ nova ainda não é encontrada pela busca. */
  const [feito, setFeito] = useState<{ texto: string; reindexar: boolean } | null>(null);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams({ status, limite: '500' });
    if (dias) params.set('dias', dias);
    try {
      setDados(await api.get<ListaLacunas>(`/admin/lacunas?${params.toString()}`));
      setErro(null);
    } catch (e) {
      setErro(explicarErro(e, 'falha ao carregar a fila'));
    }
  }, [status, dias]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function reabrir(grupo: GrupoDeLacunas): Promise<void> {
    try {
      await api.post('/admin/lacunas/reabrir', { lacunas: grupo.lacunas.map((l) => l.id) });
      setFeito({ texto: `${grupo.ocorrencias} pergunta(s) de volta à fila.`, reindexar: false });
      await carregar();
    } catch (e) {
      setErro(explicarErro(e, 'falha ao reabrir'));
    }
  }

  if (!dados) {
    return erro ? (
      <Alert color="red" variant="light" role="alert">
        {erro}
      </Alert>
    ) : (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const contagem = dados.porStatus;

  return (
    <Stack gap="sm">
      <BarraDeFiltros>
        <Select
          size="xs"
          label="Status"
          w={220}
          allowDeselect={false}
          value={status}
          onChange={(v) => setStatus((v ?? 'pendente') as StatusLacuna)}
          data={[
            { value: 'pendente', label: `Pendentes (${contagem.pendente ?? 0})` },
            { value: 'respondida', label: `Respondidas (${contagem.respondida ?? 0})` },
            { value: 'descartada', label: `Descartadas (${contagem.descartada ?? 0})` },
          ]}
        />
        <Select
          size="xs"
          label="Período"
          w={170}
          allowDeselect={false}
          value={dias}
          onChange={(v) => setDias(v ?? '')}
          data={[
            { value: '', label: 'Todo o período' },
            { value: '7', label: 'Últimos 7 dias' },
            { value: '30', label: 'Últimos 30 dias' },
            { value: '90', label: 'Últimos 90 dias' },
          ]}
        />
        <Button size="xs" onClick={() => void carregar()}>
          Recarregar
        </Button>
      </BarraDeFiltros>

      {feito && (
        <Alert color="violet" variant="light" withCloseButton onClose={() => setFeito(null)}>
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              <strong>{feito.texto}</strong>
              {feito.reindexar && (
                <>
                  {' '}
                  A pergunta <strong>ainda não é encontrada pela busca</strong>: a FAQ foi gravada
                  sem embedding e só passa a responder depois que um admin reindexar. Testar no
                  WhatsApp antes disso dá &ldquo;não encontrei&rdquo;, e não é defeito.
                </>
              )}
            </Text>
            {feito.reindexar && (
              <Button size="xs" onClick={aoIrParaFaqs}>
                Ir para FAQs e reindexar
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      {/*
        Duas seções no MESMO bloco, em vez de dois acordeões empilhados: são
        dois assuntos de verdade (como a fila funciona, como os grupos se
        formam) e um idioma só de ajuda.

        O texto dos grupos é o que já estava aqui, movido para dentro do bloco
        — não reescrito. O que a fila FAZ com o grupo é novo: o estado vazio
        explica de onde vem uma lacuna, mas só quando a fila está vazia, e a
        FAQ que nasce pendente só é avisada depois de abrir o formulário.
      */}
      <BlocoDeAjuda
        secoes={[
          {
            id: 'fila',
            titulo: 'O que fazer com cada grupo',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Cada grupo é uma dúvida que a base não respondeu.</strong> Quando o aluno
                  pergunta algo e a busca não acha resposta que sirva, a pergunta dele cai aqui com
                  as palavras que ele usou. A fila encher é sinal de uso, não de defeito: é a lista
                  do que falta escrever.
                </Text>
                <Text size="sm">
                  <strong>Duas saídas, e elas não são equivalentes.</strong>{' '}
                  <em>Criar FAQ</em> quando o assunto não existe na base — você escreve pergunta,
                  resposta e categoria, e as perguntas do grupo entram como variações.{' '}
                  <em>Adicionar a uma FAQ</em> quando a resposta JÁ existe e o aluno só escreveu
                  diferente: nada de novo é escrito, as palavras dele passam a apontar para a FAQ
                  que já estava lá. Criar FAQ repetida é pior que não criar — duas respostas para a
                  mesma dúvida disputam a busca.
                </Text>
                <Text size="sm">
                  <strong>Descartar é uma saída legítima.</strong> Pergunta fora do assunto do
                  aeroclube, teste de alguém, mensagem sem sentido: descarte com o motivo. Nada é
                  apagado — descartadas e respondidas ficam guardadas no filtro de status.
                </Text>
                <Text size="sm">
                  <strong>A FAQ que nasce aqui ainda não responde.</strong> Ela entra na base
                  pendente de reindexação e só passa a ser encontrada depois que um admin reindexar
                  na tela de FAQs. Até lá, perguntar no WhatsApp devolve “não encontrei” — o
                  formulário repete isso antes de salvar, e não é defeito.
                </Text>
              </>
            ),
          },
          {
            id: 'metodo',
            titulo: 'Como os grupos são formados — e o que eles não pegam',
            corpo: (
              <>
                <Text size="sm">
                  O agrupamento compara <strong>letras em comum</strong> (trigramas), não sentido.
                  Ele junta bem <em>reformulação</em> — &ldquo;quanto custa o curso&rdquo; e
                  &ldquo;quanto custa o curso de piloto&rdquo; — e{' '}
                  <strong>não junta sinônimo</strong>: &ldquo;qual o valor do PP&rdquo; e
                  &ldquo;quanto sai o brevê&rdquo; caem em grupos separados, mesmo sendo a mesma
                  pergunta.
                </Text>
                <Text size="sm">
                  Então <strong>dois grupos podem ser a mesma dúvida em outras palavras</strong>.
                  Antes de criar FAQ, vale olhar a lista inteira — e, se for o caso, resolver um
                  grupo com &ldquo;Criar FAQ&rdquo; e o outro com &ldquo;Adicionar a uma FAQ&rdquo;,
                  apontando para a que acabou de nascer. As duas formas de perguntar ficam indexadas
                  e as duas passam a ser encontradas.
                </Text>
              </>
            ),
          },
        ]}
      />

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}

      <Text size="sm" c="dimmed">
        {dados.grupos.length} grupo(s) · {dados.totalLacunas} pergunta(s)
      </Text>

      {dados.grupos.length === 0 &&
        (status === 'pendente' ? (
          <Vazio titulo="Nenhuma pergunta esperando.">
            A base cobriu tudo o que chegou. Uma lacuna nasce quando o aluno pergunta algo que a
            busca não encontra — então esta fila encher é sinal de uso, não de defeito: é a lista
            do que falta escrever.
          </Vazio>
        ) : (
          <Vazio titulo="Nada aqui neste filtro.">
            Volte para <strong>Pendentes</strong> para ver o que ainda espera decisão. Respondidas
            e descartadas ficam guardadas para conferência — nada é apagado.
          </Vazio>
        ))}

      {dados.grupos.map((grupo) => (
        <Grupo
          key={grupo.id}
          grupo={grupo}
          status={status}
          capacidades={capacidades}
          aberto={aberto === grupo.id}
          aoAbrir={() => setAberto(aberto === grupo.id ? null : grupo.id)}
          aoAgir={(aviso) => {
            setAberto(null);
            setFeito(aviso);
            void carregar();
          }}
          aoReabrir={() => void reabrir(grupo)}
        />
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function periodo(grupo: GrupoDeLacunas): string {
  const f = (iso: string): string => new Date(iso).toLocaleDateString('pt-BR');
  const inicio = f(grupo.primeiraEm);
  const fim = f(grupo.ultimaEm);
  return inicio === fim ? inicio : `${inicio} → ${fim}`;
}

function Grupo({
  grupo,
  status,
  capacidades,
  aberto,
  aoAbrir,
  aoAgir,
  aoReabrir,
}: {
  grupo: GrupoDeLacunas;
  status: StatusLacuna;
  capacidades: Capacidades;
  aberto: boolean;
  aoAbrir: () => void;
  aoAgir: (aviso: Aviso) => void;
  aoReabrir: () => void;
}): JSX.Element {
  const [acao, setAcao] = useState<Acao | null>(null);

  return (
    <Card withBorder padding="sm">
      <Stack gap="xs">
        <Group gap="sm" wrap="wrap">
          <Button variant="subtle" size="compact-sm" onClick={aoAbrir}>
            {aberto ? '▾' : '▸'} {grupo.pergunta}
          </Button>
          <Badge title="perguntas no grupo">×{grupo.ocorrencias}</Badge>
          <Text size="xs" c="dimmed">
            {periodo(grupo)}
          </Text>
          <Text size="xs" c="dimmed" title="melhor cosseno obtido por qualquer pergunta do grupo">
            {grupo.melhorScore === null ? 'sem score' : `máx ${grupo.melhorScore.toFixed(3)}`}
          </Text>
        </Group>

        {grupo.candidatas.length > 0 && (
          <Stack gap={2}>
            {grupo.candidatas.slice(0, 4).map((c) => (
              <Group key={c.faqId} gap={6} wrap="nowrap">
                <Badge size="xs">
                  {c.ocorrencias}/{grupo.ocorrencias}
                </Badge>
                <Text size="sm" title={`melhor cosseno ${c.melhorCosseno.toFixed(3)}`}>
                  {c.pergunta ?? '(FAQ removida)'}
                </Text>
                <Text size="xs" c="dimmed">
                  {c.melhorCosseno.toFixed(3)}
                  {c.ativa === false && ' · inativa'}
                </Text>
              </Group>
            ))}
          </Stack>
        )}

        {aberto && (
          <List type="ordered" size="sm" withPadding>
            {grupo.lacunas.map((l) => (
              <List.Item key={l.id}>
                {l.pergunta}{' '}
                <Text span size="xs" c="dimmed">
                  {new Date(l.criadoEm).toLocaleString('pt-BR')}
                  {l.melhorScore !== null && ` · ${l.melhorScore.toFixed(3)}`}
                </Text>
              </List.Item>
            ))}
          </List>
        )}

        {status === 'pendente' && capacidades.editar && (
          <Group gap="xs">
            <Button size="xs" onClick={() => setAcao(acao === 'criar' ? null : 'criar')}>
              Criar FAQ
            </Button>
            <Button size="xs" onClick={() => setAcao(acao === 'vincular' ? null : 'vincular')}>
              Adicionar a uma FAQ
            </Button>
            <Button size="xs" onClick={() => setAcao(acao === 'descartar' ? null : 'descartar')}>
              Descartar
            </Button>
          </Group>
        )}

        {status === 'descartada' && capacidades.editar && (
          <Group gap="xs">
            <Button size="xs" onClick={aoReabrir}>
              Devolver à fila
            </Button>
          </Group>
        )}

        {acao === 'criar' && <FormCriar grupo={grupo} aoConcluir={aoAgir} />}
        {acao === 'vincular' && <FormVincular grupo={grupo} aoConcluir={aoAgir} />}
        {acao === 'descartar' && <FormDescartar grupo={grupo} aoConcluir={aoAgir} />}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * Criar FAQ. As perguntas do grupo entram como variações sem o curador digitar
 * nada: ele escreve só a pergunta canônica, a categoria e a resposta.
 *
 * A resposta tem prévia do WhatsApp porque é o texto literal que chega ao
 * aluno — o LLM nunca redige nada, só escolhe qual FAQ responde.
 */
function FormCriar({
  grupo,
  aoConcluir,
}: {
  grupo: GrupoDeLacunas;
  aoConcluir: (aviso: Aviso) => void;
}): JSX.Element {
  const [pergunta, setPergunta] = useState(grupo.pergunta);
  const [categoria, setCategoria] = useState('');
  const [categorias, setCategorias] = useState<string[]>([]);
  const [resposta, setResposta] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    void api
      .get<ListaFaqs>('/admin/faqs?limite=1')
      .then((d) => setCategorias(d.categorias.map((c) => c.categoria)))
      .catch(() => undefined);
  }, []);

  // As variações ficam à vista: é o que será indexado, não um efeito oculto.
  const variacoes = grupo.lacunas
    .map((l) => l.pergunta.trim())
    .filter((p, i, todas) => p !== pergunta.trim() && todas.indexOf(p) === i);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      await api.post('/admin/lacunas/criar-faq', {
        lacunas: grupo.lacunas.map((l) => l.id),
        categoria,
        pergunta: pergunta.trim(),
        resposta,
      });
      aoConcluir({
        texto: `FAQ criada: “${pergunta.trim()}” (${grupo.ocorrencias} pergunta(s) resolvida(s)).`,
        reindexar: true,
      });
    } catch (e) {
      setErro(explicarErro(e, 'falha ao criar a FAQ'));
    } finally {
      setSalvando(false);
    }
  }

  const valido = pergunta.trim().length >= 5 && categoria !== '' && resposta.trim() !== '';

  return (
    <Stack gap="sm" mt="xs">
      <TextInput
        label="Pergunta canônica"
        value={pergunta}
        onChange={(e) => setPergunta(e.currentTarget.value)}
      />
      <Select
        label="Categoria"
        placeholder="Escolha…"
        w={260}
        value={categoria || null}
        onChange={(v) => setCategoria(v ?? '')}
        data={categorias.map((c) => ({ value: c, label: c }))}
      />
      <Textarea
        label="Resposta"
        description="é este texto, literal, que o aluno recebe"
        autosize
        minRows={5}
        maxRows={14}
        value={resposta}
        onChange={(e) => setResposta(e.currentTarget.value)}
      />
      <PreviaWhatsapp texto={resposta} />

      <Text size="xs" c="dimmed">
        {variacoes.length} variação(ões) do grupo entram junto:{' '}
        {variacoes.map((v) => `“${v}”`).join(', ') || '(nenhuma, a canônica já cobre)'}
      </Text>

      <Alert color="violet" variant="light" title="Esta FAQ não responde ainda">
        Ela nasce sem embedding, fica marcada como pendente de reindexação e só passa a ser
        encontrada pela busca depois que um admin rodar a reindexação na aba FAQs. Até lá,
        perguntar no WhatsApp devolve &ldquo;não encontrei&rdquo; — é o esperado, não defeito.
      </Alert>

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}
      <Group>
        <Button variant="filled" loading={salvando} disabled={!valido} onClick={() => void salvar()}>
          Criar FAQ e resolver o grupo
        </Button>
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------

/** Acrescenta as perguntas do grupo como variações de uma FAQ existente. */
function FormVincular({
  grupo,
  aoConcluir,
}: {
  grupo: GrupoDeLacunas;
  aoConcluir: (aviso: Aviso) => void;
}): JSX.Element {
  const [escolhida, setEscolhida] = useState(grupo.candidatas[0]?.faqId ?? '');
  const [busca, setBusca] = useState('');
  const [achadas, setAchadas] = useState<FaqNaLista[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function procurar(): Promise<void> {
    if (!busca.trim()) return;
    const params = new URLSearchParams({ busca: busca.trim(), limite: '20' });
    try {
      setAchadas((await api.get<ListaFaqs>(`/admin/faqs?${params.toString()}`)).itens);
    } catch (e) {
      setErro(explicarErro(e, 'falha na busca'));
    }
  }

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<ResultadoLacuna>('/admin/lacunas/vincular', {
        lacunas: grupo.lacunas.map((l) => l.id),
        faqId: escolhida,
      });
      aoConcluir({
        texto: `${grupo.ocorrencias} pergunta(s) adicionada(s) a “${r.faq.pergunta}”.`,
        // Variação nova é trecho novo: a FAQ volta a ficar pendente, e as
        // formas de perguntar recém-adicionadas não são achadas até reindexar.
        reindexar: r.faq.reindexacaoPendente,
      });
    } catch (e) {
      setErro(explicarErro(e, 'falha ao vincular'));
    } finally {
      setSalvando(false);
    }
  }

  const opcoes = [
    ...grupo.candidatas
      .filter((c) => c.pergunta !== null)
      .map((c) => ({
        id: c.faqId,
        rotulo: `${c.pergunta} — candidata em ${c.ocorrencias}/${grupo.ocorrencias}`,
      })),
    ...achadas
      .filter((f) => !grupo.candidatas.some((c) => c.faqId === f.id))
      .map((f) => ({ id: f.id, rotulo: `${f.pergunta} (${f.categoria})` })),
  ];

  return (
    <Stack gap="sm" mt="xs">
      <Group gap="xs" align="flex-end">
        <TextInput
          label="Buscar outra FAQ"
          w={280}
          value={busca}
          onChange={(e) => setBusca(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void procurar();
          }}
        />
        <Button onClick={() => void procurar()}>Buscar</Button>
      </Group>

      <Select
        label="FAQ que recebe as variações"
        placeholder="Escolha…"
        searchable
        value={escolhida || null}
        onChange={(v) => setEscolhida(v ?? '')}
        data={opcoes.map((o) => ({ value: o.id, label: o.rotulo }))}
      />

      <Text size="xs" c="dimmed">
        As {grupo.ocorrencias} pergunta(s) do grupo entram como variações. A resposta da FAQ não
        muda — ela continua respondendo o que já respondia. Mas as <em>novas formas de
        perguntar</em> só começam a ser encontradas depois da reindexação: variação nova é trecho
        novo, e trecho novo precisa de embedding.
      </Text>

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}
      <Group>
        <Button
          variant="filled"
          loading={salvando}
          disabled={!escolhida}
          onClick={() => void salvar()}
        >
          Adicionar as variações
        </Button>
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function FormDescartar({
  grupo,
  aoConcluir,
}: {
  grupo: GrupoDeLacunas;
  aoConcluir: (aviso: Aviso) => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      await api.post('/admin/lacunas/descartar', {
        lacunas: grupo.lacunas.map((l) => l.id),
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      });
      aoConcluir({
        texto: `${grupo.ocorrencias} pergunta(s) descartada(s).`,
        reindexar: false,
      });
    } catch (e) {
      setErro(explicarErro(e, 'falha ao descartar'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Stack gap="sm" mt="xs">
      <TextInput
        label="Motivo (opcional, mas vale a pena)"
        maxLength={300}
        placeholder="ex.: não é assunto do aeroclube"
        value={motivo}
        onChange={(e) => setMotivo(e.currentTarget.value)}
      />
      <Text size="xs" c="dimmed">
        Sem motivo, a mesma pergunta reaparece meses depois e ninguém lembra por que foi
        recusada. O descarte é reversível: o filtro “Descartadas” devolve à fila.
      </Text>

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}
      <Group>
        <Button variant="filled" loading={salvando} onClick={() => void salvar()}>
          Descartar {grupo.ocorrencias} pergunta(s)
        </Button>
      </Group>
    </Stack>
  );
}
